import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { hotelPath, hotelsPath } from 'app/hooks/data/hotels/useHotel';
import { getErrorStatus, isHttpApiError } from 'app/utils/error';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

const GATEWAY_TIMEOUT = 504;

export function useMigrateHotel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hotelId }: { hotelId: number }) => {
      try {
        return (await apiMutation('POST', {
          path: ENDPOINTS.migrateHotel,
          params: { id: hotelId.toString() },
        })) as void;
      } catch (error) {
        if (error instanceof TypeError) {
          throw new Error('Migration gateway timeout', {
            cause: { code: GATEWAY_TIMEOUT },
          });
        }

        throw error;
      }
    },
    onSuccess: (_, { hotelId }) => {
      message.success(`Hotel successfully migrated to the New Data Model.`);
      queryClient.invalidateQueries({
        queryKey: [hotelsPath],
        exact: false,
      });

      queryClient.invalidateQueries([hotelPath, { hotelId }]);
      queryClient.invalidateQueries([
        CORE_QUERY_KEYS.hotelEntities,
        { id: hotelId.toString() },
      ]);
    },
    onError: (error, { hotelId }) => {
      if (getErrorStatus(error) === GATEWAY_TIMEOUT) {
        message.warning(
          'The migration is taking longer than expected and is still running in the background. Refresh in a few minutes to check the result.'
        );

        return;
      }

      message.error(
        isHttpApiError(error)
          ? error.jsonObject.errorMessage
          : `Error while migrating hotel #${hotelId}`
      );
    },
  });
}
